import { Controller } from '@hotwired/stimulus'

// Auto Tabs Controller
//
// Drives a labeled tab system that auto-rotates between panels on a timer with
// a "fade + scale pop" transition. Only one panel is visible at a time.
//
// Rotation is driven by the active tab's CSS progress-bar animation rather than
// a JS timer: when that animation finishes (`animationend`), we advance to the
// next tab. This keeps the progress bar perfectly in sync with the switch and
// makes pause-on-hover free — toggling the `is-paused` class freezes the CSS
// animation (animation-play-state: paused), which also defers `animationend`.
//
// Hovering the active tab or the panels pauses rotation; clicking any tab stops
// it for good (the user has taken over — `is-stopped` drops the progress bar).
//
// Markup:
//   <div data-controller="auto-tabs" data-auto-tabs-interval-value="8000">
//     <div role="tablist">
//       <button data-auto-tabs-target="tab" data-index="0"
//               data-action="auto-tabs#select mouseenter->auto-tabs#pause mouseleave->auto-tabs#resume">…</button>
//       …
//     </div>
//     <div class="comparison-tabs__panels"
//          data-action="mouseenter->auto-tabs#pause mouseleave->auto-tabs#resume">
//       <div data-auto-tabs-target="panel">…</div>
//       …
//     </div>
//   </div>
export default class extends Controller {
  static targets = ['tab', 'panel', 'savingsTab', 'savingsPanel']
  static values = {
    interval: { type: Number, default: 8000 },
    active: { type: Number, default: 0 }
  }

  connect() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Keep the CSS progress animation duration in lockstep with the JS interval.
    this.element.style.setProperty('--auto-tabs-interval', `${this.intervalValue}ms`)

    this.show(this.activeValue, { animate: false })

    // Advance when the active tab's progress bar finishes filling. Animation
    // events bubble, so a single listener on the root catches the active one.
    this.onAnimationEnd = (event) => {
      if (this.stopped) return
      if (event.animationName === 'comparison-progress') this.advance()
    }
    if (!this.reducedMotion) {
      this.element.addEventListener('animationend', this.onAnimationEnd)

      // Start paused so the timer doesn't burn down while the section is
      // off-screen. Remove is-paused the first time it scrolls into view.
      this.element.classList.add('is-paused')
      this._observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.element.classList.remove('is-paused')
          this._observer.disconnect()
        }
      }, { threshold: 0.15 })
      this._observer.observe(this.element)
    }

    // Recompute the panel container height when the viewport changes.
    this.onResize = () => this.setPanelsHeight(false)
    window.addEventListener('resize', this.onResize)
  }

  disconnect() {
    this.element.removeEventListener('animationend', this.onAnimationEnd)
    window.removeEventListener('resize', this.onResize)
    this._observer?.disconnect()
  }

  // Manual tab click — the user has taken over, so stop auto-rotation for good.
  select(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (Number.isNaN(index)) return
    this.stop()
    this.show(index)
  }

  // Reveal (first run) or re-open the "Your Savings" results tab. Called by the
  // fee-calculator outlet on inline submit, and by clicking the savings tab.
  // Idempotent — safe to call repeatedly.
  showSavings() {
    if (!this.hasSavingsPanelTarget) return
    this.stop()
    this.savingsTabTarget.classList.remove('is-hidden')

    // Deactivate the rotating tables.
    this.tabTargets.forEach((tab) => {
      tab.classList.remove('is-active')
      tab.setAttribute('aria-selected', 'false')
    })
    this.panelTargets.forEach((panel) => panel.classList.remove('is-active'))

    this.savingsTabTarget.classList.add('is-active')
    this.savingsTabTarget.setAttribute('aria-selected', 'true')
    this.savingsPanelTarget.classList.add('is-active')

    this.setPanelsHeight(true)
    // Bring the results panel into view so the loader → results play is visible.
    this.savingsPanelTarget.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'center' })
  }

  // Clicking the already-revealed savings tab.
  selectSavings() {
    this.showSavings()
  }

  stop() {
    this.stopped = true
    this.element.classList.add('is-stopped')
  }

  advance() {
    if (this.panelTargets.length === 0) return
    this.show((this.activeValue + 1) % this.panelTargets.length)
  }

  pause(event) {
    // Only pause for the active tab or the table panels — hovering an inactive
    // tab (one you might be about to click) shouldn't stop the rotation.
    const target = event?.currentTarget
    if (target?.classList.contains('comparison-tabs__tab') && !target.classList.contains('is-active')) return
    this.element.classList.add('is-paused')
  }

  resume() {
    this.element.classList.remove('is-paused')
  }

  show(index, { animate = true } = {}) {
    this.activeValue = index

    this.tabTargets.forEach((tab, i) => {
      const isActive = i === index
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })

    this.panelTargets.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === index)
    })

    // Picking a comparison table deactivates the savings tab/panel.
    if (this.hasSavingsPanelTarget) {
      this.savingsTabTarget.classList.remove('is-active')
      this.savingsTabTarget.setAttribute('aria-selected', 'false')
      this.savingsPanelTarget.classList.remove('is-active')
    }

    this.setPanelsHeight(animate)
  }

  // Size the panel container to the TALLEST panel so its height stays fixed
  // across tab changes — the section never grows or shrinks while rotating.
  // (Inactive panels are absolutely positioned but still report their natural
  // content height, so we can measure all of them.) Recomputed on resize.
  setPanelsHeight(animate) {
    const container = this.panelTargets[this.activeValue]?.parentElement
    if (!container || this.panelTargets.length === 0) return

    // When the savings panel is showing, fit it exactly so the panel can grow
    // from the loader to the (taller) results. Otherwise lock to the tallest
    // table so the height stays stable while the two tables auto-rotate.
    const savingsActive = this.hasSavingsPanelTarget &&
      this.savingsPanelTarget.classList.contains('is-active')
    const maxHeight = savingsActive
      ? this.savingsPanelTarget.offsetHeight
      : Math.max(...this.panelTargets.map((panel) => panel.offsetHeight))

    if (!animate) {
      const prev = container.style.transition
      container.style.transition = 'none'
      container.style.height = `${maxHeight}px`
      // Force reflow so the suppressed transition doesn't carry into the next change.
      void container.offsetHeight
      container.style.transition = prev
      return
    }

    container.style.height = `${maxHeight}px`
  }
}
